import React from "react";
import LegalPage from "../components/LegalPage";

const sections = [
  {
    id: "agreement",
    heading: "Acceptance of terms",
    body: (
      <p>
        These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) govern your access to and use of the
        MeinHaus website and renovation-management platform (the &ldquo;<strong>Service</strong>&rdquo;) operated
        by MeinHaus Inc. (&ldquo;<strong>MeinHaus</strong>&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating
        an account, requesting a quote, or otherwise using the Service, you agree to be bound by these Terms.
      </p>
    ),
  },
  {
    id: "what-we-do",
    heading: "What the Service does (and does not) do",
    body: (
      <>
        <p>
          MeinHaus is a marketplace and project-management platform that connects homeowners with vetted,
          licensed, and insured local contractors (&ldquo;<strong>Pros</strong>&rdquo;). We facilitate the quote,
          scope, scheduling, milestone-payment, and sign-off process between you and the Pro.
        </p>
        <p>
          <strong>MeinHaus is not the contractor.</strong> The Pro you are matched with is the party performing
          the renovation work and is independently responsible for the workmanship under their contract with you.
          MeinHaus stands behind the workmanship via the Done-Right Guarantee, governed by separate{" "}
          <a
            href="/guarantee"
            className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
          >
            Guarantee Terms
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "accounts",
    heading: "Accounts &amp; eligibility",
    body: (
      <p>
        You must be at least 18 years old and a Canadian resident (or have legal authority to commission
        renovation work at a Canadian address) to use the Service. You are responsible for keeping your account
        credentials confidential and for all activity under your account. Notify us immediately of any
        unauthorized use.
      </p>
    ),
  },
  {
    id: "quotes-and-contracts",
    heading: "Quotes and the Pro–Homeowner contract",
    body: (
      <>
        <p>
          Quotes generated through MeinHaus are itemized and provided by Pros, not by MeinHaus. Once you approve
          a quote, a binding contract is formed between you and the Pro on the terms shown in the dashboard.
          MeinHaus is not a party to that contract; we facilitate it.
        </p>
        <p>
          Change orders may only be made through the dashboard and require approval from both you and the Pro
          before any new work begins.
        </p>
      </>
    ),
  },
  {
    id: "milestone-payments",
    heading: "Milestone payments",
    body: (
      <>
        <p>Payments work as follows:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>
            Funds for each milestone are deposited with MeinHaus and held safely on your behalf.
          </li>
          <li>
            When the Pro reports a milestone complete, you have a defined review window (typically 72 hours) to
            inspect the work and approve, reject with notes, or escalate to MeinHaus.
          </li>
          <li>
            Upon your approval, funds for that milestone release to the Pro, less any applicable taxes and fees
            disclosed in your quote.
          </li>
          <li>
            If you do not act within the review window, the funds release automatically. You can always pause
            this by escalating in the dashboard.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "fees",
    heading: "Fees",
    body: (
      <p>
        MeinHaus does not charge homeowners a platform fee on top of the Pro&rsquo;s quoted amount. We earn
        revenue from Pros on completed projects. Payment-processing fees, where applicable, will be disclosed
        before you confirm payment.
      </p>
    ),
  },
  {
    id: "cancellation",
    heading: "Cancellation &amp; refunds",
    body: (
      <>
        <p>
          You may cancel a project at any time before work begins for a full refund of any deposited funds.
          After work has started, refunds for unreleased milestone amounts will be processed in accordance with
          the cancellation provisions in your contract with the Pro and our Done-Right Guarantee.
        </p>
        <p>
          If a Pro cancels or fails to perform, MeinHaus will offer to re-match you with another vetted Pro at
          no additional cost or refund undeposited milestones in full.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    heading: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>misuse the Service or attempt to circumvent its security;</li>
          <li>contact Pros off-platform to avoid platform protections;</li>
          <li>upload content that is unlawful, infringing, harassing, or defamatory;</li>
          <li>use automated tools to scrape or harvest data from the Service.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ip",
    heading: "Intellectual property",
    body: (
      <p>
        The Service, including its software, design, content, and branding, is owned by MeinHaus and protected
        by intellectual-property laws. You retain ownership of project photos and content you upload, and you
        grant MeinHaus a worldwide, royalty-free license to host and display that content as needed to operate
        the Service.
      </p>
    ),
  },
  {
    id: "warranty-disclaimer",
    heading: "Warranty disclaimer",
    body: (
      <p>
        The Service is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; To the maximum extent
        permitted by law, MeinHaus disclaims all implied warranties of merchantability, fitness for a particular
        purpose, and non-infringement with respect to the platform itself. Workmanship warranties on completed
        projects are governed by the Done-Right Guarantee and the Pro&rsquo;s contractual obligations.
      </p>
    ),
  },
  {
    id: "liability",
    heading: "Limitation of liability",
    body: (
      <p>
        To the maximum extent permitted by law, MeinHaus&rsquo;s aggregate liability arising out of or relating
        to the Service or these Terms will not exceed the greater of (a) the total fees paid to MeinHaus for
        the relevant project in the 12 months preceding the claim, or (b) CAD $500. We are not liable for
        indirect, consequential, or punitive damages.
      </p>
    ),
  },
  {
    id: "governing-law",
    heading: "Governing law &amp; disputes",
    body: (
      <p>
        These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada
        applicable therein. The courts located in Toronto, Ontario have exclusive jurisdiction over any dispute,
        subject to any non-waivable rights you may have as a consumer.
      </p>
    ),
  },
  {
    id: "changes",
    heading: "Changes",
    body: (
      <p>
        We may update these Terms from time to time. Material changes will be communicated by email or in the
        dashboard. Continued use of the Service after changes take effect constitutes acceptance.
      </p>
    ),
  },
];

const TermsOfService = () => (
  <LegalPage
    eyebrow="Terms"
    title="Terms of Service"
    lede="The rules of the road for using MeinHaus. We've kept the legalese tight and the meaning plain."
    updated="February 8, 2026"
    sections={sections}
  />
);

export default TermsOfService;
